import type { GameInput, InputRejection, RuleEvent, Transition } from './rules/contracts';
import type {
  GameModeId,
  Json,
  MatchPhase,
  MatchResult,
  PlayerId,
  SeatIndex,
  Timestamp,
} from './types';

/**
 * The wire contract. Both sides import these names; neither side types a
 * message string by hand.
 *
 * THE convention, and it is not negotiable: **phases travel as absolute
 * timestamps**. `PhaseEnvelope.startsAt` is a moment on the server clock. The
 * client converts it to local time through its measured offset and schedules
 * against that. It never receives "3000ms from now" and never chains
 * `setTimeout` to walk from one phase to the next - that drifts, stalls when
 * the tab is backgrounded, and desynchronises a versus match within seconds.
 */

// -----------------------------------------------------------------------------
// Message names
// -----------------------------------------------------------------------------

export const CLIENT_MSG = {
  /** Clock handshake probe. Payload: ClockPing. */
  CLOCK_PING: 'clock.ping',
  /** Player is loaded and ready to leave the ready substate of `lobby`. */
  READY: 'room.ready',
  /** A player action. Payload: GameInput. */
  INPUT: 'game.input',
  /** Voluntary surrender. */
  FORFEIT: 'game.forfeit',
  /** Ask to play again after `ended`. */
  REMATCH: 'game.rematch',
} as const;

export const SERVER_MSG = {
  /** Reply to CLOCK_PING. Payload: ClockPong. */
  CLOCK_PONG: 'clock.pong',
  /** Seat assignment and match identity, sent once on join. Payload: JoinInfo. */
  JOINED: 'room.joined',
  /** Phase change, with absolute boundaries. Payload: PhaseEnvelope. */
  PHASE: 'room.phase',
  /** What changed on the board and where it came from. Payload: TransitionMessage. */
  TRANSITIONS: 'game.transitions',
  /** An input the server refused. Payload: RejectionMessage. */
  REJECTED: 'game.rejected',
  /** Final result. Payload: MatchResult. */
  RESULT: 'game.result',
  /** New match identity after both players accepted a rematch. */
  RESET: 'game.reset',
  /** Achievements the server just unlocked for this player. Payload: AchievementUnlockMessage. */
  ACHIEVEMENTS: 'game.achievements',
} as const;

export type ClientMessageName = (typeof CLIENT_MSG)[keyof typeof CLIENT_MSG];
export type ServerMessageName = (typeof SERVER_MSG)[keyof typeof SERVER_MSG];

// -----------------------------------------------------------------------------
// Clock handshake
// -----------------------------------------------------------------------------

export interface ClockPing {
  /** Echoed back untouched, so late replies can be matched to their probe. */
  readonly id: number;
  /** Client clock at send. The server never interprets it, only returns it. */
  readonly clientSentAt: Timestamp;
}

export interface ClockPong {
  readonly id: number;
  readonly clientSentAt: Timestamp;
  /** Server clock at the moment the ping was handled. */
  readonly serverTime: Timestamp;
}

// -----------------------------------------------------------------------------
// Room lifecycle
// -----------------------------------------------------------------------------

export interface JoinInfo {
  readonly matchId: string;
  readonly modeId: GameModeId;
  readonly ruleSetId: string;
  readonly seat: SeatIndex;
  readonly playerId: PlayerId | null;
  /** Four-character invitation code. Present only in private rooms. */
  readonly roomCode?: string;
  /** Server clock at the moment of the join, so the client can bootstrap early. */
  readonly serverTime: Timestamp;
}

/**
 * A phase, with its boundaries as absolute server timestamps.
 *
 * `startsAt` may be in the future: that is how a synchronised countdown works -
 * both clients are told "playing begins at T" and both schedule against their
 * own converted T, instead of each starting a timer when their packet happened
 * to arrive.
 */
export interface PhaseEnvelope {
  readonly phase: MatchPhase;
  /** Monotonic. A client that sees a lower seq than it has ignores the message. */
  readonly seq: number;
  readonly startsAt: Timestamp;
  /** Null when the phase has no scheduled end (waiting for players, etc.). */
  readonly endsAt: Timestamp | null;
  /** Server clock at send time. Lets the client sanity-check its offset for free. */
  readonly serverTime: Timestamp;
  /** Phase-specific extras: who is missing, why we are waiting, ... */
  readonly meta?: Readonly<Record<string, Json>>;
}

// -----------------------------------------------------------------------------
// Gameplay
// -----------------------------------------------------------------------------

export interface TransitionMessage {
  readonly seq: number;
  readonly seat: SeatIndex;
  readonly transitions: readonly Transition[];
  readonly events: readonly RuleEvent[];
  /** Server time this batch should start animating at. */
  readonly at: Timestamp;
  /** Ack of the client input that caused it, for prediction reconciliation. */
  readonly causedBy?: number;
}

export interface RejectionMessage {
  /** The `seq` of the GameInput being refused. */
  readonly inputSeq: number;
  readonly rejection: InputRejection;
  /** Server time, so the client can decide whether the rejection is still relevant. */
  readonly at: Timestamp;
}

/**
 * Sent only to the player who earned them, only after the server has already
 * written them. The client renders the toast from its own catalogue copy, which
 * is why this carries ids rather than names.
 */
export interface AchievementUnlockMessage {
  readonly matchId: string;
  readonly seat: SeatIndex;
  readonly ids: readonly string[];
  readonly at: Timestamp;
}

export interface MatchResetInfo {
  readonly matchId: string;
  readonly seed: string;
  readonly startAt: Timestamp;
  readonly serverTime: Timestamp;
}

// -----------------------------------------------------------------------------
// Typed payload map - the reason neither side hand-types a message string.
// -----------------------------------------------------------------------------

export interface ClientPayloads {
  [CLIENT_MSG.CLOCK_PING]: ClockPing;
  [CLIENT_MSG.READY]: Record<string, never>;
  [CLIENT_MSG.INPUT]: GameInput;
  [CLIENT_MSG.FORFEIT]: Record<string, never>;
  [CLIENT_MSG.REMATCH]: Record<string, never>;
}

export interface ServerPayloads {
  [SERVER_MSG.CLOCK_PONG]: ClockPong;
  [SERVER_MSG.JOINED]: JoinInfo;
  [SERVER_MSG.PHASE]: PhaseEnvelope;
  [SERVER_MSG.TRANSITIONS]: TransitionMessage;
  [SERVER_MSG.REJECTED]: RejectionMessage;
  [SERVER_MSG.RESULT]: MatchResult;
  [SERVER_MSG.RESET]: MatchResetInfo;
  [SERVER_MSG.ACHIEVEMENTS]: AchievementUnlockMessage;
}
